/* Astrography reader — opens the `<dialog>` rendered by
   snippets/ag-reader.liquid. A trigger anywhere on the page carries
   `data-ag-reader-open="<the dialog's id>"`, so the teaser and the modal do
   not need to know about each other. */

class AgReader extends HTMLElement {
  #abort = null;
  #opener = null;

  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    if (!this.dialog) return;

    this.scroller = this.querySelector('[data-ag-reader-scroll]');
    this.bar = this.querySelector('[data-ag-reader-bar]');

    this.#abort = new AbortController();
    const { signal } = this.#abort;

    document.addEventListener('click', this.#onDocumentClick, { signal });
    this.dialog.addEventListener('click', this.#onDialogClick, { signal });
    this.dialog.addEventListener('close', this.#onClose, { signal });
    this.scroller?.addEventListener('scroll', this.#onScroll, { passive: true, signal });
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#abort = null;
  }

  open(opener) {
    if (!this.dialog || this.dialog.open) return;

    this.#opener = opener ?? null;
    this.#activateEmbeds();

    this.dialog.showModal();
    // `showModal` alone leaves iOS scrolling the page behind the dialog.
    document.documentElement.style.overflow = 'hidden';

    if (this.scroller) this.scroller.scrollTop = 0;
    this.#onScroll();
    if (this.scroller) {
      // Focus is parked here so arrow keys scroll the text straight away. But
      // Chrome counts a focused scroll container as focus-visible, so that
      // parked focus drew a ring around the whole body the moment the reader
      // opened. Suppress it for this one focus only: the class goes as soon as
      // focus leaves, so a keyboard user who tabs back still sees the ring.
      this.scroller.classList.add('is-quiet-focus');
      this.scroller.addEventListener(
        'blur',
        () => this.scroller.classList.remove('is-quiet-focus'),
        { once: true, signal: this.#abort?.signal },
      );
      this.scroller.focus({ preventScroll: true });
    }
  }

  close() {
    this.dialog?.close();
  }

  /* A video embed ships with `data-ag-src` instead of `src` so that a page
     with the modal shut never asks YouTube for anything. First open swaps
     them back; later opens find nothing left to do. */
  #activateEmbeds() {
    for (const frame of this.querySelectorAll('iframe[data-ag-src]')) {
      frame.src = frame.dataset.agSrc;
      delete frame.dataset.agSrc;
    }
  }

  #onDocumentClick = (event) => {
    const trigger = event.target.closest?.(`[data-ag-reader-open="${CSS.escape(this.id)}"]`);
    if (!trigger) return;
    event.preventDefault();
    this.open(trigger);
  };

  #onDialogClick = (event) => {
    if (event.target.closest('[data-ag-reader-close]')) return this.close();
    // The dialog box itself is the only child; a click that lands on the
    // dialog element is a click on the backdrop area around it.
    if (event.target === this.dialog) this.close();
  };

  #onClose = () => {
    document.documentElement.style.overflow = '';
    // Pause a playing embed by reloading it; the player has no API here.
    for (const frame of this.querySelectorAll('iframe[src]')) {
      frame.src = frame.src; // eslint-disable-line no-self-assign
    }
    this.#opener?.focus({ preventScroll: true });
    this.#opener = null;
  };

  #onScroll = () => {
    if (!this.bar || !this.scroller) return;
    const travel = this.scroller.scrollHeight - this.scroller.clientHeight;
    const ratio = travel > 0 ? Math.min(1, this.scroller.scrollTop / travel) : 0;
    this.bar.style.transform = `scaleX(${ratio})`;
  };
}

if (!customElements.get('ag-reader')) customElements.define('ag-reader', AgReader);
